import { LoadingButton } from '@mui/lab';
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextareaAutosize,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { useBulkRejectQueueItemsMutation } from '@/store/api/services/marketing/queue';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';

import type { QueueItem } from '@/types';

const INELIGIBLE_REASONS: Partial<Record<QueueItem['status'], string>> = {
  rejected: 'Already rejected',
  approved: 'Already approved',
  completed: 'Already completed',
  error: 'Error state',
  obsolete: 'Obsolete',
  conflict: 'Has conflict',
};

const BulkRejectQueue = ({ hide, props }: ModalChildrenProps) => {
  const selectedItems = (props?.data?.selectedItems ?? []) as QueueItem[];
  const [reason, setReason] = useState('');
  const [bulkReject, { isLoading, isSuccess }] = useBulkRejectQueueItemsMutation();

  const { eligible, ineligible } = useMemo(() => {
    const eligible: QueueItem[] = [];
    const ineligible: { item: QueueItem; reason: string }[] = [];
    for (const item of selectedItems) {
      const reason = INELIGIBLE_REASONS[item.status];
      if (reason) ineligible.push({ item, reason });
      else eligible.push(item);
    }
    return { eligible, ineligible };
  }, [selectedItems]);

  const handleSubmit = async () => {
    try {
      await bulkReject({ ids: eligible.map((i) => i.id), reason }).unwrap();
      toast.success(`${eligible.length} queue item(s) rejected.`);
    } catch (_err) {
      toast.error('Failed to reject queue items.');
    }
  };

  return (
    <>
      <DialogContent sx={{ py: 2 }}>
        {!isSuccess ? (
          <>
            <Typography variant="h6" mb={1}>
              Reject {eligible.length} queue item(s)?
            </Typography>
            {ineligible.length > 0 && (
              <Typography variant="body2" color="text.secondary" mb={2}>
                {ineligible.length} selected item(s) will be skipped — only <b>Pending</b> items can
                be rejected.
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" mb={1}>
              Please provide a reason for rejecting all selected queue items.
            </Typography>
            <TextareaAutosize
              minRows={2}
              maxRows={3}
              placeholder="Enter reason for rejection..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              style={{
                width: '100%',
                resize: 'vertical',
                padding: '12px 14px',
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: '4px',
                fontSize: '0.875rem',
                fontFamily: 'inherit',
                lineHeight: '1.5',
                backgroundColor: 'transparent',
                outline: 'none',
                cursor: 'text',
              }}
            />
            {ineligible.length > 0 && (
              <>
                <Divider sx={{ mt: 2 }} />
                <TableContainer
                  sx={{ mt: 2, opacity: isLoading ? 0.5 : 1, transition: 'opacity 0.3s' }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Location</TableCell>
                        <TableCell>Action</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {eligible.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.location_name ?? item.location_id.toString()}</TableCell>
                          <TableCell>{item.action}</TableCell>
                          <TableCell>{item.status}</TableCell>
                        </TableRow>
                      ))}
                      {ineligible.map(({ item, reason }) => (
                        <Tooltip key={item.id} title={reason} placement="left">
                          <TableRow sx={{ opacity: 0.4 }}>
                            <TableCell sx={{ textDecoration: 'line-through' }}>
                              {item.location_name ?? item.location_id.toString()}
                            </TableCell>
                            <TableCell sx={{ textDecoration: 'line-through' }}>
                              {item.action}
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.disabled">
                                {reason}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </Tooltip>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        ) : (
          <Typography variant="h6">{eligible.length} queue item(s) rejected.</Typography>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {!isSuccess ? (
          <>
            <Button onClick={hide}>Cancel</Button>
            <LoadingButton
              variant="contained"
              color="error"
              onClick={handleSubmit}
              loading={isLoading}
              disabled={!reason.trim() || eligible.length === 0}
            >
              Reject {eligible.length > 0 ? eligible.length : ''} item(s)
            </LoadingButton>
          </>
        ) : (
          <Button fullWidth onClick={hide}>
            Back to Queue
          </Button>
        )}
      </DialogActions>
    </>
  );
};

export default BulkRejectQueue;
